import { Redirect, Stack, useLocalSearchParams } from "expo-router";
import { ActivityIndicator } from "react-native";

import { CenteredScreen } from "~/components/ui";
import { useAuth } from "~/hooks/useAuth";
import { normalizeAuthRedirectPath } from "~/utils/auth-redirect";
import { getPostAuthPath } from "~/utils/onboarding";

export default function AuthLayout() {
  const { session, user, loading, passwordRecoveryActive } = useAuth();
  const params = useLocalSearchParams<{ callbackURL?: string }>();

  if (loading) {
    return (
      <CenteredScreen>
        <ActivityIndicator />
      </CenteredScreen>
    );
  }

  if (session?.user) {
    if (passwordRecoveryActive) {
      return <Redirect href="/reset-password" />;
    }
    // The session can land a beat before the profile does (a fresh sign-up
    // or sign-in resolves `session` first, then `user` loads separately).
    // getPostAuthPath(null) resolves to "/login", so redirecting before
    // `user` arrives here bounces straight back to the screen we're trying
    // to leave. Wait for it instead.
    if (!user) {
      return (
        <CenteredScreen>
          <ActivityIndicator />
        </CenteredScreen>
      );
    }
    // This is the ONLY place login/register should navigate from once
    // signed in. login.tsx/register.tsx used to also call router.replace()
    // themselves right after login()/register() resolved -- but that races
    // this guard's own re-render: the manual navigation could land on a
    // (protected) route before React committed the session/user state
    // update, so ProtectedLayout's identical guard would see the stale
    // `session: null` and bounce straight back to /login. Redirecting only
    // here, reactively, off the same state this component just re-rendered
    // from, makes that ordering bug impossible by construction.
    const fallback = getPostAuthPath(user);
    const destination = params.callbackURL
      ? normalizeAuthRedirectPath(params.callbackURL, fallback)
      : fallback;
    return <Redirect href={destination as never} />;
  }

  return (
    <Stack
      screenOptions={{
        headerShown: false,
      }}
    />
  );
}
